<!-- 地图选择弹窗组件：基于高德地图 JS API v2.0 实现 -->
<template>
  <Dialog
    title="高德地图"
    v-model="dialogVisible"
    :fullscreen="false"
    :scroll="false"
    :body-scroll="false"
    width="800px"
    @opened="handleDialogOpened"
    @closed="handleDialogClosed"
  >
    <div class="w-full">
      <!-- 第一行：位置搜索 -->
      <el-form label-width="80px">
        <el-form-item label="定位位置">
          <el-select
            class="w-full"
            v-model="state.address"
            clearable
            filterable
            remote
            reserve-keyword
            placeholder="可输入地址查询经纬度"
            :remote-method="autoSearch"
            @change="handleAddressSelect"
            :loading="state.loading"
          >
            <el-option
              v-for="item in state.mapAddressOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <!-- 第二行：坐标显示 -->
        <el-form-item label="当前坐标">
          <div class="flex items-center gap-4">
            <span>经度: {{ state.longitude || '-' }}</span>
            <span>纬度: {{ state.latitude || '-' }}</span>
          </div>
        </el-form-item>
      </el-form>
      <!-- 第三行：地图（勿放进 ElScrollbar，否则易灰底无瓦片） -->
      <div
        v-if="state.mapContainerReady"
        ref="mapContainerRef"
        class="amap-dialog__map"
      ></div>
      <div v-else class="amap-dialog__map amap-dialog__map--loading">
        <span class="text-gray-400">地图加载中...</span>
      </div>
      <div v-if="state.loadError" class="mt-8px text-12px text-red-500">{{ state.loadError }}</div>
    </div>
    <template #footer>
      <el-button
        @click="handleConfirm"
        type="primary"
        :disabled="!state.longitude || !state.latitude"
      >
        确 定
      </el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive, ref, nextTick } from 'vue'
import { loadAmapSdk } from './amapUtils'

const emits = defineEmits(['confirm'])

/** 国内常用范围（GCJ-02）；境外坐标用国内 Key 常会灰底无瓦片 */
const CN_LNG_MIN = 73
const CN_LNG_MAX = 136
const CN_LAT_MIN = 3
const CN_LAT_MAX = 54
const SHENZHEN_CENTER: [number, number] = [114.057868, 22.543099]

const dialogVisible = ref(false)
const mapContainerRef = ref<HTMLElement>()
const state = reactive({
  address: '',
  loading: false,
  latitude: '',
  longitude: '',
  pca: { province: '', city: '', district: '' },
  map: null as any,
  mapAddressOptions: [] as any[],
  mapMarker: null as any,
  geocoder: null as any,
  placeSearch: null as any,
  mapContainerReady: false,
  loadError: ''
})

const initLongitude = ref<number | undefined>()
const initLatitude = ref<number | undefined>()

function isValidCnLngLat(lng?: number, lat?: number): boolean {
  if (lng == null || lat == null) return false
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false
  return lng >= CN_LNG_MIN && lng <= CN_LNG_MAX && lat >= CN_LAT_MIN && lat <= CN_LAT_MAX
}

/** 打开弹窗；非法/境外坐标会忽略，避免国内 Key 灰底 */
const open = (longitude?: number, latitude?: number) => {
  const lng = longitude != null ? Number(longitude) : NaN
  const lat = latitude != null ? Number(latitude) : NaN
  if (isValidCnLngLat(lng, lat)) {
    initLongitude.value = lng
    initLatitude.value = lat
    state.longitude = String(lng)
    state.latitude = String(lat)
  } else {
    initLongitude.value = undefined
    initLatitude.value = undefined
    state.longitude = ''
    state.latitude = ''
  }
  state.address = ''
  state.mapAddressOptions = []
  state.loadError = ''
  dialogVisible.value = true
}

defineExpose({ open })

const handleDialogOpened = async () => {
  state.mapContainerReady = true
  state.loadError = ''
  await nextTick()
  try {
    await loadAmapSdk()
    // 等弹窗动画/布局稳定后再建图，并强制 resize（嵌套 Dialog 常见灰底）
    await new Promise((r) => setTimeout(r, 80))
    initMapInstance()
    requestAnimationFrame(() => {
      state.map?.resize?.()
      setTimeout(() => state.map?.resize?.(), 200)
    })
  } catch (e: any) {
    state.loadError = e?.message || '高德地图加载失败，请检查 VITE_AMAP_KEY / 安全密钥与域名白名单'
  }
}

const handleDialogClosed = () => {
  if (state.map) {
    state.map.destroy?.()
    state.map = null
  }
  state.mapMarker = null
  state.geocoder = null
  state.placeSearch = null
  state.mapContainerReady = false
  state.loadError = ''
}

const initMapInstance = () => {
  if (!mapContainerRef.value || !window.AMap) {
    return
  }

  initMap()
  initGeocoder()
  initPlaceSearch()

  state.map.on('click', (e: any) => {
    const lng = e.lnglat.getLng()
    const lat = e.lnglat.getLat()
    regeoCode(lng, lat)
  })

  if (isValidCnLngLat(initLongitude.value, initLatitude.value)) {
    regeoCode(initLongitude.value as number, initLatitude.value as number)
  }
}

const initMap = () => {
  const hasInit = isValidCnLngLat(initLongitude.value, initLatitude.value)
  const center = hasInit
    ? ([initLongitude.value, initLatitude.value] as [number, number])
    : SHENZHEN_CENTER

  state.map = new window.AMap.Map(mapContainerRef.value, {
    zoom: hasInit ? 16 : 11,
    center,
    viewMode: '2D'
  })

  state.map.addControl(new window.AMap.ToolBar({ position: 'RB' }))
  state.map.addControl(new window.AMap.Scale())
}

const initGeocoder = () => {
  state.geocoder = new window.AMap.Geocoder({ city: '全国' })
}

const initPlaceSearch = () => {
  // 不限制仅深圳，全国门店可选点
  state.placeSearch = new window.AMap.PlaceSearch({ city: '全国', citylimit: false })
}

const autoSearch = (queryValue: string) => {
  if (!queryValue) {
    state.mapAddressOptions = []
    return
  }
  if (!state.placeSearch) {
    return
  }

  state.loading = true
  state.placeSearch.search(queryValue, (status: string, result: any) => {
    state.loading = false
    if (status !== 'complete' || !result.poiList) {
      state.mapAddressOptions = []
      return
    }
    state.mapAddressOptions = (result.poiList.pois || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      address: p.address,
      location: p.location,
      pname: p.pname,
      cityname: p.cityname,
      adname: p.adname
    }))
  })
}

const handleAddressSelect = (value: string) => {
  if (!value) return
  const selected = state.mapAddressOptions.find((p: any) => p.id === value)
  if (selected && selected.location) {
    const lng = selected.location.getLng()
    const lat = selected.location.getLat()
    regeoCode(lng, lat, selected.name + (selected.address ? '（' + selected.address + '）' : ''))
    state.pca = {
      province: selected.pname || '',
      city: selected.cityname || '',
      district: selected.adname || ''
    }
  }
}

const setMarker = (lng: number, lat: number) => {
  if (!state.map) return
  if (state.mapMarker !== null) {
    state.map.remove(state.mapMarker)
  }
  state.mapMarker = new window.AMap.Marker({
    position: [lng, lat],
    draggable: true
  })
  state.mapMarker.on('dragend', (e: any) => {
    const newLng = e.lnglat.getLng()
    const newLat = e.lnglat.getLat()
    regeoCode(newLng, newLat)
  })
  state.map.add(state.mapMarker)
  state.map.setCenter([lng, lat])
  state.map.setZoom(16)
}

const regeoCode = (lng: number, lat: number, prefilledAddress?: string) => {
  state.longitude = String(lng)
  state.latitude = String(lat)
  setMarker(lng, lat)
  if (prefilledAddress) {
    state.address = prefilledAddress
  } else {
    getAddress(lng, lat)
  }
}

const getAddress = (lng: number, lat: number) => {
  if (!state.geocoder) return
  state.geocoder.getAddress([lng, lat], (status: string, result: any) => {
    if (status !== 'complete' || !result) return
    const regeo = result.regeocode || (Array.isArray(result.regeocodes) ? result.regeocodes[0] : null)
    if (!regeo) return

    state.address = regeo.formattedAddress || regeo.formatted_address || ''

    const ac = regeo.addressComponent || {}
    const province = ac.province || ''
    const cityRaw = ac.city
    const city =
      typeof cityRaw === 'string'
        ? cityRaw
        : Array.isArray(cityRaw)
          ? cityRaw[0]
          : province || ''
    const district = ac.district || ''
    state.pca = { province, city, district }
  })
}

const handleConfirm = () => {
  if (state.longitude && state.latitude) {
    emits('confirm', {
      longitude: parseFloat(state.longitude),
      latitude: parseFloat(state.latitude),
      address: state.address,
      province: state.pca.province,
      city: state.pca.city,
      district: state.pca.district
    })
  }
  dialogVisible.value = false
}
</script>

<style scoped>
.amap-dialog__map {
  width: 100%;
  height: 420px;
  margin-top: 10px;
  background: #f5f5f5;
}

.amap-dialog__map--loading {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
